import React, { useEffect, useState } from "react";
import { maintenanceApi, StationDropdownItem, SocketTypeItem } from "../../../api/maintenance";
import { X, Plus, Trash2 } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface DeviceFormProps {
  onClose: () => void;
  onCreated: () => void;
}

interface ConnectorEntry {
  connector_name: string;
  socket_type_id: number | "";
  capacity_kwh: string;
}

const DeviceForm: React.FC<DeviceFormProps> = ({ onClose, onCreated }) => {
  const [stations, setStations] = useState<StationDropdownItem[]>([]);
  const [socketTypes, setSocketTypes] = useState<SocketTypeItem[]>([]);

  const [uniqueName, setUniqueName] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ocppVersion, setOcppVersion] = useState("1.6");
  const [webSocketAddr, setWebSocketAddr] = useState("");
  const [stationId, setStationId] = useState<number | "">("");
  const [stationPosition, setStationPosition] = useState(0);
  const [connectors, setConnectors] = useState<ConnectorEntry[]>([
    { connector_name: "1", socket_type_id: "", capacity_kwh: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useNotice("error", "");

  useEffect(() => {
    Promise.all([
      maintenanceApi.getStationsDropdown(),
      maintenanceApi.getSocketTypes(),
    ]).then(([stationsRes, socketRes]) => {
      setStations(stationsRes.data);
      setSocketTypes(socketRes.data);
    }).catch(console.error);
  }, []);

  const addConnector = () => {
    setConnectors([
      ...connectors,
      { connector_name: String(connectors.length + 1), socket_type_id: "", capacity_kwh: "" },
    ]);
  };

  const removeConnector = (idx: number) => {
    if (connectors.length <= 1) return;
    setConnectors(connectors.filter((_, i) => i !== idx));
  };

  const updateConnector = (idx: number, field: keyof ConnectorEntry, value: any) => {
    const updated = [...connectors];
    updated[idx] = { ...updated[idx], [field]: value };
    setConnectors(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uniqueName.trim() || !deviceId.trim() || !webSocketAddr.trim()) {
      setError("Unique Name, OCPP Device ID, and WebSocket Address are required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await maintenanceApi.createDevice({
        unique_name: uniqueName.trim(),
        device_id: deviceId.trim(),
        display_name: displayName.trim() || undefined,
        ocpp_version: ocppVersion,
        web_socker_add: webSocketAddr.trim(),
        station_id: stationId ? (stationId as number) : undefined,
        station_position: stationId ? stationPosition : undefined,
        connectors: connectors.map((c) => ({
          connector_name: c.connector_name,
          socket_type_id: c.socket_type_id ? (c.socket_type_id as number) : undefined,
          capacity_kwh: c.capacity_kwh ? parseFloat(c.capacity_kwh) : undefined,
        })),
      });
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create device");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card maint-form-card--wide">
        <div className="maint-form-header">
          <h3>Add Device</h3>
          <button onClick={onClose} className="maint-btn-icon"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="maint-form-row">
            <label>
              Unique Name *
              <input type="text" value={uniqueName} onChange={(e) => setUniqueName(e.target.value)} required />
            </label>
            <label>
              OCPP Device ID *
              <input type="text" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} required />
            </label>
          </div>
          <div className="maint-form-row">
            <label>
              Display Name
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label>
              OCPP Version
              <select value={ocppVersion} onChange={(e) => setOcppVersion(e.target.value)}>
                <option value="1.6">1.6</option>
                <option value="2.0">2.0</option>
                <option value="2.0.1">2.0.1</option>
              </select>
            </label>
          </div>
          <label>
            WebSocket Address *
            <input type="text" value={webSocketAddr} onChange={(e) => setWebSocketAddr(e.target.value)} placeholder="ws://..." required />
          </label>
          <div className="maint-form-row">
            <label>
              Station (optional)
              <select value={stationId} onChange={(e) => setStationId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">No station</option>
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.org_name})</option>
                ))}
              </select>
            </label>
            {stationId && (
              <label>
                Position
                <input type="number" value={stationPosition} onChange={(e) => setStationPosition(parseInt(e.target.value) || 0)} />
              </label>
            )}
          </div>

          {/* Connectors */}
          <div style={{ marginTop: "12px", marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <strong>Connectors</strong>
              <button type="button" className="maint-btn-small" onClick={addConnector}>
                <Plus size={14} /> Add
              </button>
            </div>
            {connectors.map((c, idx) => (
              <div key={idx} style={{ display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: "8px" }}>
                <label style={{ width: "70px" }}>
                  Name
                  <input type="text" value={c.connector_name} onChange={(e) => updateConnector(idx, "connector_name", e.target.value)} />
                </label>
                <label style={{ flex: 1 }}>
                  Socket Type
                  <select value={c.socket_type_id} onChange={(e) => updateConnector(idx, "socket_type_id", e.target.value ? Number(e.target.value) : "")}>
                    <option value="">Select...</option>
                    {socketTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </label>
                <label style={{ width: "100px" }}>
                  kWh
                  <input type="number" step="any" value={c.capacity_kwh} onChange={(e) => updateConnector(idx, "capacity_kwh", e.target.value)} />
                </label>
                <button type="button" className="maint-btn-icon" onClick={() => removeConnector(idx)} disabled={connectors.length <= 1} style={{ marginBottom: "4px" }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {error && <p className="maint-form-error">{error}</p>}
          <div className="maint-form-actions">
            <button type="button" onClick={onClose} className="maint-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="maint-btn-primary">
              {submitting ? "Creating..." : "Create Device"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceForm;
